/**
 * KeyImageOverlay — gold star badge rendered into the `#viewport-0` container
 * whenever the currently-displayed image is flagged as a key image.
 *
 * Uses createPortal so the badge tracks the viewport div regardless of layout
 * changes and z-orders above the Cornerstone canvas and the text overlays.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconStar } from './ToolIcons';
import styles from './KeyImageOverlay.module.css';

interface Props {
  visible: boolean;
}

export default function KeyImageOverlay({ visible }: Props) {
  const [host, setHost] = useState<HTMLElement | null>(null);

  // Resolve the viewport container lazily — it's mounted by ViewportGrid on a
  // later tick than ViewerPage, so poll briefly until it appears.
  useEffect(() => {
    if (host) return;
    let tries = 0;
    const i = setInterval(() => {
      const el = document.getElementById('viewport-0');
      if (el) { setHost(el); clearInterval(i); return; }
      if (++tries > 40) clearInterval(i);
    }, 100);
    return () => clearInterval(i);
  }, [host]);

  if (!visible || !host) return null;
  return createPortal(
    <div className={styles.badge} title="This image is flagged as a key image">
      <IconStar size={20} />
    </div>,
    host
  );
}
